import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Scissors } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import type { Hospital, Surgery } from '../types/clinical';

interface LogbookSurgery extends Surgery {
  patientName: string;
}

interface ProcedureGroup {
  name: string;
  count: number;
}

export function ProcedureLogbookPage() {
  const navigate = useNavigate();
  const { get } = useApi();
  const { userRole } = useAuth();

  const [surgeries, setSurgeries] = useState<LogbookSurgery[]>([]);
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [filterHospital, setFilterHospital] = useState('');
  const [filterProcedure, setFilterProcedure] = useState('');
  const [filterAnaesthesia, setFilterAnaesthesia] = useState('');
  const [filterCaseType, setFilterCaseType] = useState<'' | 'elective' | 'emergency'>('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterComplicationsOnly, setFilterComplicationsOnly] = useState(false);

  // Detail view
  const [selectedProcedure, setSelectedProcedure] = useState<string | null>(null);

  const fetchSurgeries = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filterHospital) params.set('hospital', filterHospital);
      if (filterStartDate) params.set('startDate', filterStartDate);
      if (filterEndDate) params.set('endDate', filterEndDate);
      const res = await get<LogbookSurgery[]>(`/clinical/logbook?${params}`);
      if (res.success && res.data) setSurgeries(res.data);
      else setError(res.error || 'Failed to load logbook');
    } catch {
      setError('Failed to load logbook');
    } finally {
      setLoading(false);
    }
  }, [get, filterHospital, filterStartDate, filterEndDate]);

  useEffect(() => { fetchSurgeries(); }, [fetchSurgeries]);

  useEffect(() => {
    (async () => {
      const res = await get<Hospital[]>('/config/hospitals');
      if (res.success && res.data) setHospitals(res.data.filter(h => h.active));
    })();
  }, [get]);

  // Client-side filters
  const filtered = useMemo(() => {
    let result = surgeries;
    if (filterProcedure) result = result.filter(s => s.procedure === filterProcedure);
    if (filterAnaesthesia) result = result.filter(s => s.anaesthesiaType === filterAnaesthesia);
    if (filterCaseType) result = result.filter(s => s.caseType === filterCaseType);
    if (filterComplicationsOnly) result = result.filter(s => s.complications?.trim());
    return result;
  }, [surgeries, filterProcedure, filterAnaesthesia, filterCaseType, filterComplicationsOnly]);

  // Distinct values for filter dropdowns
  const distinctProcedures = useMemo(() =>
    Array.from(new Set(surgeries.map(s => s.procedure).filter(Boolean))).sort(),
  [surgeries]);

  const distinctAnaesthesia = useMemo(() =>
    Array.from(new Set(surgeries.map(s => s.anaesthesiaType).filter(Boolean))).sort(),
  [surgeries]);

  // Grouped procedures
  const procedureGroups = useMemo((): ProcedureGroup[] => {
    const counts: Record<string, number> = {};
    for (const s of filtered) {
      const key = s.procedure || 'Unspecified';
      counts[key] = (counts[key] || 0) + 1;
    }
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [filtered]);

  // Detail rows for selected procedure
  const detailRows = useMemo(() =>
    selectedProcedure ? filtered.filter(s => (s.procedure || 'Unspecified') === selectedProcedure) : [],
  [filtered, selectedProcedure]);

  const resetFilters = () => {
    setFilterHospital('');
    setFilterProcedure('');
    setFilterAnaesthesia('');
    setFilterCaseType('');
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterComplicationsOnly(false);
  };

  if (userRole !== 'doctor') {
    return <main className="max-w-7xl mx-auto px-4 py-8"><p className="text-gray-500">Doctor access only.</p></main>;
  }

  return (
    <main className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Scissors className="h-6 w-6 text-teal-600" />
        <h1 className="text-2xl font-bold text-gray-900">Surgical Logbook</h1>
      </div>

      {error && <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Filter sidebar */}
        <div className="md:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-4">
            <h3 className="text-sm font-semibold text-gray-700">Filters</h3>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Hospital</label>
              <select value={filterHospital} onChange={e => setFilterHospital(e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white">
                <option value="">All</option>
                {hospitals.map(h => <option key={h.id} value={h.name}>{h.name}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Procedure</label>
              <select value={filterProcedure} onChange={e => setFilterProcedure(e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white">
                <option value="">All</option>
                {distinctProcedures.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Anaesthesia Type</label>
              <select value={filterAnaesthesia} onChange={e => setFilterAnaesthesia(e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white">
                <option value="">All</option>
                {distinctAnaesthesia.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Case Type</label>
              <select
                value={filterCaseType}
                onChange={e => setFilterCaseType(e.target.value as '' | 'elective' | 'emergency')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white"
              >
                <option value="">All</option>
                <option value="elective">Elective</option>
                <option value="emergency">Emergency</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Date Range</label>
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={filterStartDate} onChange={e => setFilterStartDate(e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm" placeholder="From" />
                <input type="date" value={filterEndDate} onChange={e => setFilterEndDate(e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm" placeholder="To" />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={filterComplicationsOnly} onChange={e => setFilterComplicationsOnly(e.target.checked)} className="h-4 w-4 rounded" />
              Only with complications
            </label>

            <div className="flex items-center justify-between pt-2">
              <button onClick={resetFilters} className="text-sm text-blue-600 hover:text-blue-800">Reset Filters</button>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="md:col-span-2">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>
          ) : !selectedProcedure ? (
            /* Summary view */
            <div>
              <div className="bg-teal-600 text-white px-4 py-2 rounded-t-lg font-semibold flex justify-between items-center">
                <span>Procedures</span>
                <span className="bg-white text-teal-700 rounded-full px-2.5 py-0.5 text-sm font-semibold">{filtered.length}</span>
              </div>
              {procedureGroups.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-b-lg p-8 text-center text-gray-500">No surgeries found.</div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-b-lg divide-y divide-gray-100">
                  {procedureGroups.map(g => (
                    <button
                      key={g.name}
                      onClick={() => setSelectedProcedure(g.name)}
                      className="w-full px-4 py-3 flex justify-between items-center hover:bg-gray-50 cursor-pointer text-left"
                    >
                      <span className="text-sm font-medium text-gray-900">{g.name}</span>
                      <span className="bg-teal-600 text-white rounded-full px-2.5 py-0.5 text-sm font-semibold">{g.count}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Detail view */
            <div>
              <button onClick={() => setSelectedProcedure(null)} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 mb-3">
                <ArrowLeft className="h-4 w-4" /> Back to summary
              </button>
              <div className="bg-teal-600 text-white px-4 py-2 rounded-t-lg font-semibold flex justify-between items-center">
                <span>{selectedProcedure}</span>
                <span className="bg-white text-teal-700 rounded-full px-2.5 py-0.5 text-sm font-semibold">{detailRows.length}</span>
              </div>
              <div className="bg-white border border-gray-200 rounded-b-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hospital</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Anaesthesia</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">EBL</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Complications</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {detailRows.map(s => (
                      <tr key={s.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{s.date}</td>
                        <td className="px-4 py-3 text-sm">
                          <button onClick={() => navigate(`/clinical/${s.patientId}`)} className="text-blue-600 hover:underline">{s.patientName || 'Unknown'}</button>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">{s.hospital || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{s.anaesthesiaType || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{s.ebl || '—'}</td>
                        <td className="px-4 py-3 text-sm">
                          {s.complications?.trim()
                            ? <span className="text-red-700">{s.complications}</span>
                            : <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">None</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
